// src/apollo/manualPreviewClient.js
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  defaultDataIdFromObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import fetch from "isomorphic-fetch";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/**
 * Recursively add __typename (from typeHandle) and inherit siteId when
 * it is missing. This guarantees Apollo can build the cache key
 * (id + siteId) for every object, even when nested fragments don’t
 * request siteId explicitly.
 */
function enrich(obj, siteId) {
  if (Array.isArray(obj)) return obj.map((v) => enrich(v, siteId));

  if (obj && typeof obj === 'object') {
    const copy = { ...obj };

    if (copy.typeHandle && !copy.__typename) copy.__typename = copy.typeHandle;
    if (copy.id && copy.siteId == null) copy.siteId = siteId;

    Object.keys(copy).forEach((k) => {
      copy[k] = enrich(copy[k], siteId);
    });
    return copy;
  }
  return obj;
}

/** Apollo link that runs `enrich()` on each response */
const enrichLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const opSiteId = Number(operation.variables?.siteId) || 1; // default site
    if (response.data) response.data = enrich(response.data, opSiteId);
    return response;
  })
);

/* ------------------------------------------------------------------ */
/*  Factory                                                            */
/* ------------------------------------------------------------------ */

/**
 * Create an Apollo Client suitable for Craft **Preview** pages.
 *
 * @param {string} [token]            Bearer / preview token
 * @param {string} [xCraftPreview]    Optional X-Craft-Preview header
 * @param {string} [xCraftLivePreview] Optional X-Craft-Live-Preview header
 * @param {string} [apiUrl]           Override GraphQL endpoint;
 *                                    defaults to GATSBY_CRAFT_API_IMPORTEXPORT_URL
 * @returns {ApolloClient}
 */
export function createManualClient(
  token,
  xCraftPreview,
  xCraftLivePreview,
  apiUrl
) {
  const uri = apiUrl ?? process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL;
  const FALLBACK_TOKEN = process.env.GATSBY_CRAFT_API_EXPORT_TOKEN;

  /* ---------------- HTTP + auth links ---------------- */
  const httpLink = createHttpLink({ uri, fetch });

  // X-Craft-Token / X-Craft-Authorization are Craft preview-draft tokens and
  // must only be sent when a real preview token is present. Craft 5.8+ rejects
  // requests that send a plain GraphQL bearer token in X-Craft-Token (400).
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: `Bearer ${token ?? FALLBACK_TOKEN}`,
      ...(token ? {
        "X-Craft-Authorization": `Bearer ${token}`,
        "X-Craft-Token": token,
      } : {}),
    },
  }));

  // GOCHA: This means siteId needs to be returned with all entry queries, else there will be cache conflicts:
  const cache = new InMemoryCache({
    dataIdFromObject(obj) {
      // only attach IDs to objects that have a typename AND an id
      if (obj && obj.__typename && obj.id !== undefined) {
        const site = obj.siteId ?? 1; // ← default locale
        return `${obj.__typename}:${obj.id}:${site}`;
      }
      // defer to Apollo's default for everything else
      return defaultDataIdFromObject(obj);
    },
  });

  /* ---------------- assemble & return client --------- */
  return new ApolloClient({
    link: ApolloLink.from([authLink, enrichLink, httpLink]),
    cache,
  });
}

/* default export so you can:  import createManualClient from '…' */
export default createManualClient;
